import { z } from 'zod';

/*
 * Importing the old website's contact list — owner request 2026-08-07,
 * **rewritten 2026-08-10 after the owner read what it had actually decided.**
 *
 * The first version had one destination. Every row it took became a *customer*,
 * and every row it did not take was thrown away — so an enquiry from last month
 * was either promoted to customer, which is a lie, or lost, which is a waste.
 * The choice offered was a filter over that single outcome ("customers",
 * "customers and subscribers", "everyone"), which is why it looked like a
 * decision about *how many* when the real question was *where each one goes*.
 *
 * **What the 508-row file actually contains**, counted rather than assumed:
 *
 *   * **16 people booked a clean.** `Last activity type` is `booking_created`.
 *     That is the only hard evidence of a customer anywhere in the file, and
 *     all 16 are also flagged `Customer`.
 *   * **39 are flagged `Customer`** — so 23 of them never booked anything. They
 *     opened a login, were sent a membership email, or confirmed a newsletter
 *     signup. That is a mailing-list state, not a purchase, and importing those
 *     23 as customers is what made the customer list untrustworthy.
 *   * **372 filled in a web form.** Only 35 did so in the last 90 days; 99 are
 *     more than two years old.
 *   * **74 did nothing at all** — an email address, sometimes a name.
 *   * **50 are newsletter subscribers**, none of whom ever booked.
 *   * **Nobody has an address**, so no property is created for anybody.
 *   * 488 distinct people; 18 email addresses appear twice.
 *
 * So the rule is **route on what the person did, not on the old platform's
 * flag**, into the two places this application already has — and keep the rest
 * rather than deleting it.
 */

/**
 * Where a row ends up. Every row gets exactly one of these, and the preview
 * shows the count beside each before anything is written.
 */
export const IMPORT_DESTINATIONS = ['client', 'lead_new', 'lead_lost', 'skipped'] as const;
export type ImportDestination = (typeof IMPORT_DESTINATIONS)[number];

export const IMPORT_DESTINATION_LABELS: Record<ImportDestination, string> = {
  client: 'Customers',
  lead_new: 'Leads — New',
  lead_lost: 'Leads — Lost',
  skipped: 'Not imported',
};

export const IMPORT_DESTINATION_HINTS: Record<ImportDestination, string> = {
  client: 'They booked a clean. The only evidence of a real customer in the file.',
  lead_new: 'They asked the business something recently. A queue worth working through.',
  lead_lost:
    'Everybody else — old enquiries, newsletter signups, logins. Kept and searchable, out of the queue. One click puts any of them back.',
  skipped: 'Nothing usable, or already here.',
};

/** Why a row was not imported at all. Every one is shown to the person importing. */
export type SkipReason = 'no_contact' | 'no_name' | 'suppressed' | 'already_a_client';

export const SKIP_REASON_LABELS: Record<SkipReason, string> = {
  no_contact: 'No email address and no phone number',
  no_name: 'No name',
  /**
   * **They asked not to be emailed.** Importing them as an ordinary customer
   * would put them back on the list the export says they left, which is the one
   * mistake in this whole feature with a legal edge to it.
   *
   * Nobody in the 2026-08-07 file is suppressed. The check stays because the
   * next file is not this file.
   */
  suppressed: 'Unsubscribed or bounced in the old system',
  already_a_client: 'Already a customer here',
};

/**
 * How recent an enquiry has to be to arrive as a lead worth working.
 *
 * **The only judgement left in the import, so it is the only control.**
 * Everything else follows from what the row did. 90 days is the default because
 * a cleaning enquiry goes cold quickly and it produces a queue somebody will
 * actually finish — 35 people out of this file, against 147 at a year.
 */
export const FRESH_WINDOWS = [30, 90, 365] as const;
export type FreshWindow = (typeof FRESH_WINDOWS)[number];

export const FRESH_WINDOW_LABELS: Record<FreshWindow, string> = {
  30: 'Last 30 days',
  90: 'Last 90 days',
  365: 'Last year',
};

export interface ImportRowPreview {
  /** 1-based, counting the header as row 1 — what a spreadsheet shows. */
  line: number;
  name: string;
  email: string | null;
  phone: string | null;
  destination: ImportDestination;
  /** Set only when the destination is `skipped`. */
  skipReason: SkipReason | null;
  /** True when two or more rows were folded into this one. */
  merged: boolean;
  /** They agreed to be emailed marketing. Carried onto whatever record is made. */
  marketingOptIn: boolean;
}

export interface ImportPreview {
  /** Every column the file had, so somebody can see what was ignored. */
  headers: string[];
  totalRows: number;
  /** How many rows land in each place. The whole point of the screen. */
  countsByDestination: Record<ImportDestination, number>;
  /** Duplicate emails folded together. */
  mergedRows: number;
  /**
   * How many carry a marketing opt-in, wherever they land.
   *
   * **Shown because it is the one fact in the file that cannot be recovered
   * later** — owner decision 2026-08-10. There is no newsletter in this system
   * (business-rules.md §8) and none is planned; this records who agreed, so the
   * day somebody builds one the answer is a query rather than an apology.
   */
  marketingOptIns: number;
  /**
   * True when the file has no address columns with anything in them, so no
   * properties will be created. Said out loud — an import that silently makes
   * customers with no address looks broken.
   */
  hasNoAddresses: boolean;
  rows: ImportRowPreview[];
}

export interface ImportResult {
  clientsCreated: number;
  leadsCreated: number;
  skipped: number;
  /** Already present, matched on email. Safe to run the same file twice. */
  alreadyPresent: number;
  marketingOptIns: number;
}

export const clientImportSchema = z.object({
  /** Days. See `FRESH_WINDOWS` — the only judgement the screen still asks for. */
  freshDays: z.coerce.number().int().positive().max(3650).default(90).catch(90),
  /**
   * False previews, true writes. One parser, so the two cannot disagree.
   *
   * **Not `z.coerce.boolean()`, and this is not a style preference.** Multipart
   * carries strings, and `Boolean('false')` is `true` — so a form field reading
   * `commit=false` parsed as *commit*, and every dry run wrote the whole file
   * to the customer list. Caught by opening the dialog and noticing the people
   * from the preview were already in the list behind it.
   *
   * The same trap is called out by name in the calendar filter's schema, which
   * was written first and got it right. An explicit pair of strings is the only
   * shape that cannot do this.
   */
  commit: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),
});

export type ClientImportInput = z.infer<typeof clientImportSchema>;
